/**
 * The organizations rail on the credentials page.
 *
 * Rendered through `ContextRail`, the frame Desk's rail uses, so the two
 * contextual rails are one object: a tinted column with a hairline right
 * edge, a grab strip to resize it, a chevron that folds it to icons,
 * eyebrow-voiced band headings, and the same neutral wash for hover and for
 * the selected row. `CredentialsRail` mounts it beside `<main>`; this file is
 * what it lists.
 *
 * **Banded by the top of the tree.** "All accounts" sits alone at the top,
 * then one band per top-level organization: its name is the band's heading,
 * and its children are the rows. A distributor sees a band per MSP with that
 * MSP's companies beneath, so two MSPs' companies are visible at once and
 * nothing is hidden behind a click the reader has not made. Where the tree
 * has a third level (a platform admin's distributors), a row expands in place
 * to its companies, and the path to the current selection opens by itself.
 *
 * Selecting a node narrows the list to that subtree; selecting the selected
 * node, or "All accounts", clears it. Nothing selected shows everything, so
 * the page is never empty because of navigation.
 */
import { useState, type ElementType } from "react";
import { Building, Building2, ChevronRight, Key, Layers } from "lucide-react";

import {
  ContextRail,
  RailBand,
  RailBandHeading,
  RailButton,
  RailIcon,
  RailIconGroup,
} from "@/components/layout/ContextRail";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  pathToSelection,
  type OrgNode,
  type OrgNodeKind,
  type OrgSelection,
} from "./orgTree";

/** The same glyph per level as the Organizations console's tree. */
const NODE_ICON: Record<OrgNodeKind, ElementType> = {
  distributor: Layers,
  msp: Building,
  company: Building2,
};

/**
 * The frame, shared with the skeleton so the column does not change size when
 * the tree arrives. The keys are the ones this rail had inside the page, so a
 * width or fold set there carries over.
 */
const FRAME = {
  title: "Organizations",
  label: "Organizations",
  foldLabel: "organizations",
  widthKey: "ec-credentials-rail-w-v1",
  foldKey: "ec-credentials-rail-collapsed-v1",
  defaultWidth: 240,
  minWidth: 180,
  maxWidth: 400,
} as const;

const nodeKey = (node: Pick<OrgNode, "kind" | "id">) =>
  `${node.kind}:${node.id}`;

const isSelected = (node: OrgNode, selection: OrgSelection | null) =>
  selection?.kind === node.kind && selection.id === node.id;

const needs = (n: number) => `${n} need${n === 1 ? "s" : ""} attention`;

/** "North MSP, 7 accounts, 2 need attention", for a folded icon. */
function spokenName(label: string, count: number, attention: number): string {
  const accounts = `${count} account${count === 1 ? "" : "s"}`;
  return attention > 0
    ? `${label}, ${accounts}, ${needs(attention)}`
    : `${label}, ${accounts}`;
}

/**
 * The one signal the tree carries besides its counts: something beneath this
 * node needs a person. The count stays neutral, as it does on Desk's rail.
 */
function AttentionDot({ attention }: { attention: number }) {
  if (attention <= 0) return null;
  return (
    <span
      role="img"
      className="bg-destructive inline-block h-1.5 w-1.5 shrink-0 rounded-full"
      aria-label={needs(attention)}
    />
  );
}

export interface OrgNavigatorProps {
  tree: OrgNode[];
  selection: OrgSelection | null;
  onSelect: (selection: OrgSelection | null) => void;
  /** Credentials in scope overall, for the "All accounts" row. */
  total: number;
  totalAttention: number;
  /**
   * Whether a band's heading is a choice. False when the one band is the
   * viewer's own organization, as for an MSP admin: choosing their MSP is
   * what "All accounts" already means, and two controls for one choice make
   * the reader wonder what the difference is.
   */
  bandsSelectable?: boolean;
}

export function OrgNavigator({
  tree,
  selection,
  onSelect,
  total,
  totalAttention,
  bandsSelectable = true,
}: OrgNavigatorProps) {
  // The reader's own open/close choices. A node they have not touched is open
  // when it lies on the path to the selection, so a company chosen in the
  // scope bar is visible here without anyone unfolding its MSP.
  const [toggled, setToggled] = useState<Map<string, boolean>>(new Map());
  const onPath = pathToSelection(tree, selection);
  const isOpen = (key: string) => toggled.get(key) ?? onPath.has(key);
  const toggle = (key: string) =>
    setToggled((prev) =>
      new Map(prev).set(key, !(prev.get(key) ?? onPath.has(key))),
    );

  // Clicking the selected node clears it. Without this the only way back to
  // the whole estate is the row at the top, which is easy to miss once the
  // rail has scrolled.
  const choose = (node: OrgNode) =>
    onSelect(
      isSelected(node, selection) ? null : { kind: node.kind, id: node.id },
    );

  const nodeIcon = (node: OrgNode, active: boolean) => (
    <RailIcon
      key={nodeKey(node)}
      icon={NODE_ICON[node.kind]}
      name={node.label}
      ariaLabel={spokenName(node.label, node.count, node.attention)}
      count={node.count}
      note={
        node.attention > 0 && (
          <span className="text-destructive">{needs(node.attention)}</span>
        )
      }
      attention={node.attention > 0}
      active={active}
      onSelect={() => choose(node)}
    />
  );

  // Folded: "All accounts" and one icon per band, each wearing its count.
  // Desk folds every surface to an icon, but an estate can hold hundreds of
  // companies and a column of identical building glyphs says nothing, so the
  // fold keeps the bands and drops the rows under them. The band that holds
  // the selection carries the wash, so the reader still sees where the list
  // is narrowed to. A band that is not a choice (an MSP admin's own MSP) would
  // be a second "All accounts", so its rows stand in for it.
  const folded = (
    <>
      <RailIconGroup>
        <RailIcon
          icon={Key}
          name="All accounts"
          ariaLabel={spokenName("All accounts", total, totalAttention)}
          count={total}
          note={
            totalAttention > 0 && (
              <span className="text-destructive">{needs(totalAttention)}</span>
            )
          }
          attention={totalAttention > 0}
          active={selection === null}
          onSelect={() => onSelect(null)}
        />
      </RailIconGroup>
      {tree.length > 0 && (
        <RailIconGroup divided>
          {tree.flatMap((band) =>
            bandsSelectable
              ? [
                  nodeIcon(
                    band,
                    isSelected(band, selection) || onPath.has(nodeKey(band)),
                  ),
                ]
              : band.children.map((node) =>
                  nodeIcon(
                    node,
                    isSelected(node, selection) || onPath.has(nodeKey(node)),
                  ),
                ),
          )}
        </RailIconGroup>
      )}
    </>
  );

  return (
    <ContextRail {...FRAME} folded={folded}>
      <div className="space-y-0.5">
        <RailButton
          icon={Key}
          label="All accounts"
          count={total}
          marker={<AttentionDot attention={totalAttention} />}
          selected={selection === null}
          onSelect={() => onSelect(null)}
        />
      </div>

      {/* No bands for an empty tree: `CredentialsRail` does not draw the rail
          at all then, and "All accounts" alone is still true. */}
      {tree.map((band) => (
        <RailBand
          key={nodeKey(band)}
          heading={
            <RailBandHeading
              label={band.label}
              count={band.count}
              marker={<AttentionDot attention={band.attention} />}
              selected={bandsSelectable && isSelected(band, selection)}
              onSelect={bandsSelectable ? () => choose(band) : undefined}
            />
          }
        >
          {/* An organization with nothing under it keeps its heading and
                its zero, so the rail agrees with the org console about what
                exists. */}
          {band.children.map((node) => (
            <TreeRows
              key={nodeKey(node)}
              node={node}
              depth={0}
              selection={selection}
              isOpen={isOpen}
              onToggle={toggle}
              onChoose={choose}
            />
          ))}
        </RailBand>
      ))}
    </ContextRail>
  );
}

/** A node and, when it is open, the nodes under it. */
function TreeRows({
  node,
  depth,
  selection,
  isOpen,
  onToggle,
  onChoose,
}: {
  node: OrgNode;
  depth: number;
  selection: OrgSelection | null;
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
  onChoose: (node: OrgNode) => void;
}) {
  const key = nodeKey(node);
  const expandable = node.children.length > 0;
  const open = expandable && isOpen(key);
  return (
    <>
      <RailButton
        icon={NODE_ICON[node.kind]}
        label={node.label}
        count={node.count}
        marker={<AttentionDot attention={node.attention} />}
        depth={depth}
        selected={isSelected(node, selection)}
        onSelect={() => onChoose(node)}
        trailing={
          expandable && (
            <button
              type="button"
              onClick={() => onToggle(key)}
              aria-expanded={open}
              aria-label={`${open ? "Collapse" : "Expand"} ${node.label}`}
              className="text-muted-foreground hover:text-foreground rounded p-1"
            >
              <ChevronRight
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  open && "rotate-90",
                )}
              />
            </button>
          )
        }
      />
      {open &&
        node.children.map((child) => (
          <TreeRows
            key={nodeKey(child)}
            node={child}
            depth={depth + 1}
            selection={selection}
            isOpen={isOpen}
            onToggle={onToggle}
            onChoose={onChoose}
          />
        ))}
    </>
  );
}

/**
 * The rail, before the data.
 *
 * The same frame as the real thing (the stored width or the fold, the same
 * header, rows on the same 36px rhythm), so when the tree arrives nothing
 * moves. The header renders for real: "Organizations" is known before any
 * request, and blanking out something already true makes a loading state
 * busier than the page.
 */
export function OrgNavigatorSkeleton() {
  return (
    <ContextRail
      {...FRAME}
      busy
      folded={
        <div className="flex flex-col items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-9 w-9 items-center justify-center">
              <Skeleton className="h-4 w-4" />
            </div>
          ))}
        </div>
      }
    >
      <div className="flex h-9 items-center gap-2.5 px-3">
        <Skeleton className="h-4 w-4 shrink-0" />
        <Skeleton className="h-3.5 w-24" />
      </div>
      <div className="mt-4 space-y-0.5">
        <div className="px-3 py-1">
          <Skeleton className="h-2.5 w-20" />
        </div>
        {/* Widths vary a little so the block does not read as a table of
            identical bars, but only a little. */}
        {["w-28", "w-24", "w-20"].map((w) => (
          <div key={w} className="flex h-9 items-center gap-2.5 px-3">
            <Skeleton className="h-4 w-4 shrink-0" />
            <Skeleton className={cn("h-3.5", w)} />
          </div>
        ))}
      </div>
    </ContextRail>
  );
}
